import React from 'react';
import classNames from 'classnames';
import { useFormatDate } from 'hooks';
import { FormattedMessage } from 'react-intl';

import ErrorMessage from 'components/ErrorMessage';

export const getFormatMessage = (_entity, key, options) => {
  const { i18nEntityName } = options;

  return <FormattedMessage id={`${i18nEntityName}.${key}`} />;
  // return useFormatMessage(`${i18nEntityName}.${key}`);
};

export const getDateMessage = (_entity, key) => {
  return useFormatDate(_entity[key]);
};

const getValue = (_entity, schema, key, options) => {
  let res = '';
  if (!_entity || !schema || !key) {
    return res;
  }

  if (typeof schema[key] === 'function') {
    res = schema[key](_entity, schema.key, options);
  } else {
    // Get the value from schema, or get the key in schema and get from entity
    res = schema[key] || _entity[schema.key];
  }
  // return `${res || ''}`; // Stringify
  return res || '';
};

export const parseEntityDataToComponents = (entity, fields = [], options) => {
  return fields.map((field) => {
    const { key } = field;

    // If label is not defined,
    // then do not show this field.
    if (!field.label) {
      return null;
    }
    return (
      <div className="is-flex" key={key}>
        <div className="column is-narrow">
          <h3 className="subtitle">
            {getValue(entity, field, 'label', options)}
          </h3>
        </div>
        <div
          className="column"
          title={getValue(entity, field, 'contentTitle', options)}
        >
          <p> {getValue(entity, field, 'content', options)}</p>
        </div>
      </div>
    );
  });
};

// FORM

/**
 * From a schema, build a Component
 */
const getFormFieldComponent = (
  _schemaForm,
  errors,
  register,
  entity,
  entityOptions
) => {
  const { options } = _schemaForm;
  const hasController = !!_schemaForm.controller;
  const isHidden = _schemaForm.type === 'hidden';
  const isBoolean = _schemaForm.type === 'boolean';
  const isSelect = _schemaForm.type === 'select';
  const isFieldset = _schemaForm.type === 'fieldset';
  const isOtherType = !hasController && !isBoolean && !isSelect && !isFieldset;

  return isHidden ? (
    <input
      name={_schemaForm.name}
      id={_schemaForm.id}
      className={classNames('input', {
        'is-danger': errors[_schemaForm.name],
      })}
      ref={register}
      type={_schemaForm.type}
      readOnly={_schemaForm.readonly}
      disabled={_schemaForm.disabled}
      {...options} // eslint-disable-line react/jsx-props-no-spreading
      key={_schemaForm.id}
    />
  ) : (
    <div className="field is-horizontal" key={_schemaForm.id}>
      <div className="field-label is-normal">
        <label className="label" htmlFor={_schemaForm.name}>
          {_schemaForm.label(_schemaForm, _schemaForm.id, entityOptions)}
        </label>
      </div>
      <div className="field-body">
        <div className="field">
          {hasController && _schemaForm.controller()}
          {isBoolean && (
            <div className="control">
              <input
                name={_schemaForm.name}
                id={_schemaForm.id}
                type="checkbox"
                ref={register}
              />
              <label htmlFor={_schemaForm.name} className="checkbox is-size-4">
                &nbsp;
              </label>
            </div>
          )}
          {isFieldset && (
            <div className="control">
              <fieldset>
                {_schemaForm
                  .inputs(entity, _schemaForm.id, entityOptions)
                  .map((input, index) => {
                    return (
                      <div key={input.value}>
                        <label>
                          <input
                            name={`${_schemaForm.name}.${index}`}
                            id={_schemaForm.id}
                            type="checkbox"
                            ref={register}
                            value={input.value}
                            defaultChecked={input.checked}
                          />
                          &nbsp;{input.label}
                        </label>
                      </div>
                    );
                  })}
              </fieldset>
            </div>
          )}
          {isSelect && (
            <div className="select">
              <select
                name={_schemaForm.name}
                id={_schemaForm.id}
                className={classNames('input', {
                  'is-danger': errors[_schemaForm.name],
                })}
                ref={register}
                readOnly={_schemaForm.readonly}
                disabled={_schemaForm.disabled}
                defaultValue={entity[_schemaForm.id]}
                {...options} // eslint-disable-line react/jsx-props-no-spreading
              >
                {_schemaForm
                  .inputs(entity, _schemaForm.id, entityOptions)
                  .map((input) => {
                    return (
                      <option key={input.value} value={input.value}>
                        {input.label}
                      </option>
                    );
                  })}
              </select>
            </div>
          )}
          {isOtherType && (
            <div className="control">
              <input
                name={_schemaForm.name}
                id={_schemaForm.id}
                placeholder={_schemaForm.placeholder}
                className={classNames('input', {
                  'is-danger': errors[_schemaForm.name],
                })}
                ref={register}
                type={_schemaForm.type}
                readOnly={_schemaForm.readonly}
                disabled={_schemaForm.disabled}
                {...options} // eslint-disable-line react/jsx-props-no-spreading
              />
            </div>
          )}
        </div>
      </div>
      {errors[_schemaForm.name] && (
        <div className="field is-horizontal">
          <div className="field-label is-normal" />
          <div className="field-body">
            <ErrorMessage />
          </div>
        </div>
      )}
    </div>
  );
};

export const getFormFieldsComponents = (
  schemas,
  errors,
  register,
  action = 'edit',
  entity,
  entityOptions
) => {
  return schemas.map((_schema) => {
    let { _form } = _schema;
    if (action === 'edit' && _schema._editForm) {
      _form = _schema._editForm;
    }
    if (action === 'create' && _schema._createForm) {
      _form = _schema._createForm;
    }
    if (!_form) {
      return null;
    }

    // If type is object, dive into children
    if (_form.type === 'object') {
      const properties = Object.keys(_form.properties);
      return properties.map((property) => {
        const propertySchema = _form.properties[property];
        return getFormFieldComponent(
          propertySchema,
          errors,
          register,
          entity,
          entityOptions
        );
      });
    }
    return getFormFieldComponent(
      _form,
      errors,
      register,
      entity,
      entityOptions
    );
  });
};

export const getCardComponent = (card) => {
  const { header, fields } = card;
  return (
    <div key={header.title}>
      {' '}
      <header className="card-header">
        <p className="card-header-title">
          <span className="icon">
            <i className={`${header.icon} default`} />
          </span>
          {header.title}
        </p>
      </header>
      <div className="card-content">{fields}</div>
    </div>
  );
};
